// 라벨 가이드(가이드 툴팁) 단일 정의 파일.
//
// reserved 라벨 입력 옵션의 툴팁과 설명이 이 파일의 문구를 쓴다. 라벨 칩 hover 툴팁은
// `labelExplanation` 이 고른다. 서버 `description` 이 먼저이고 이 파일의 문구는 폴백이다.
// 라벨→가이드 매핑은 이 파일 한 곳에만 둔다.
//
// 소유 경계: FE 는 **한국어 표시 문구(짧은 라벨, 사유)만 소유**한다. 어느 값이 존재하는지는 서버
// 계약이 SoT 다. quality 는 `GET /v1/review-options`, source allowlist 는
// `GET /v1/label-reserved-keys` 다. 문구가 없는 값은 값 자체를 라벨로 쓰고 가이드 없이 표시한다.
// 그래서 계약에 새 값이 먼저 들어와도 깨지지 않는다.

import { QUALITY_KEY, reservedAllowlistSnapshot } from "@/api";
import { episodeQualityTagTip } from "./episodeQualityTags";

// reserved 라벨 축의 표시 문구. key -> value -> { ko, tip }. `ko` 는 칩과 facet 에 표시하는 짧은
// 라벨, `tip` 은 가이드 툴팁의 사유다. 에피소드 quality 축(`episodeQualityTags.ts`)과 같은 구조를
// 쓴다. 그래서 한 라벨의 두 문구를 따로 관리하지 않는다.
// 키 순서는 계약을 받지 못했을 때 fallback vocab 의 표시 순서가 된다(계약 배열 순서와 맞춘다).
const RESERVED_COPY: Readonly<
  Record<string, Readonly<Record<string, { ko: string; tip: string }>>>
> = {
  // 환경 축. real ↔ simulation.
  environment: {
    real: { ko: "실제", tip: "실제 로봇으로 수집한 데이터입니다." },
    simulation: { ko: "시뮬레이션", tip: "시뮬레이션·벤치마크 환경의 데이터입니다." },
  },
  // 출처 축. 수집 주체 기준 분류.
  source: {
    open_source: { ko: "오픈소스", tip: "공개된 오픈소스 Dataset입니다." },
    internal: { ko: "내부", tip: "내부에서 직접 수집한 Dataset입니다." },
    alba: { ko: "알바", tip: "알바나 외주로 수집한 Dataset입니다." },
    customer: { ko: "고객", tip: "고객이 제공한 Dataset입니다." },
  },
};

// (key, value) 의 한국어 사유. 없으면 null 이다. 칩 툴팁의 폴백과 reserved 옵션 설명이 같은 문구를
// 쓴다.
//
// quality 값은 에피소드에만 붙는다. 그래서 문구는 `episodeQualityTags` 가 소유한다. 여기서 연결하지
// 않으면 에피소드 값(manipulation_failed 등)이 라벨 칩에서만 문구를 잃는다.
export function labelGuideSummary(key: string, value: string): string | null {
  if (key === QUALITY_KEY) {
    return episodeQualityTagTip(value);
  }
  return RESERVED_COPY[key]?.[value]?.tip ?? null;
}

// reserved (key, value) 의 짧은 한국어 라벨. 문구를 두지 않은 값은 null 이므로 호출자가 자기
// fallback 을 고른다. 그래서 계약에 새 값이 먼저 들어와도 화면에서 값이 사라지지 않는다.
export function reservedValueLabel(key: string, value: string): string | null {
  return RESERVED_COPY[key]?.[value]?.ko ?? null;
}

// source(데이터 소스) reserved allowlist 값 목록. 수집 주체 분류다. 데이터셋 필터 '데이터 소스'
// facet 이 이 vocab 을 쓴다. 계약을 아직 받지 못했으면 사유 문구가 있는 값을 대신 쓴다.
export function sourceLabelValues(): string[] {
  const allowlist = reservedAllowlistSnapshot("source");
  return allowlist === null ? Object.keys(RESERVED_COPY.source ?? {}) : [...allowlist];
}
